/**
 * React render-storm latch (G2) — detect repeated #185 signatures, pause UI churn, resume on nav/retry.
 * Pattern mirrors httpStormDetectedLatch (detect → pause → resume).
 */

import { quarantineCartRegistryOnRenderStorm } from './cartRegistryStormRecovery.js';

export const REACT_MAX_UPDATE_DEPTH_PATTERN = /185|Maximum update depth exceeded/i;

/** Sliding window for repeated identical signatures before arming. */
export const RENDER_STORM_WINDOW_MS = 5_000;

/** Hits within window required to arm latch. */
export const RENDER_STORM_HIT_THRESHOLD = 2;

/** Default latch TTL after arm. */
export const RENDER_STORM_LATCH_DEFAULT_MS = 60_000;

type RenderStormListener = () => void;

interface RenderStormLatchState {
  latched: boolean;
  expiresAtMs: number;
  fingerprint: string | null;
}

let state: RenderStormLatchState = {
  latched: false,
  expiresAtMs: 0,
  fingerprint: null,
};

const listeners = new Set<RenderStormListener>();
const hitTimestamps = new Map<string, number[]>();

function notifyListeners(): void {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // Subscribers must not break the latch bus.
    }
  }
}

function expireIfNeeded(now: number = Date.now()): void {
  if (state.latched && now >= state.expiresAtMs) {
    state = { latched: false, expiresAtMs: 0, fingerprint: null };
  }
}

export function fingerprintRenderStormError(error: Error, source?: string): string {
  const message = error.message.slice(0, 200);
  const stackHead = (error.stack ?? '').split('\n').slice(0, 3).join('|').slice(0, 300);
  return `${message}::${stackHead}::${source ?? ''}`;
}

export function isReactMaximumUpdateDepthError(error: unknown): boolean {
  if (error instanceof Error) {
    if (REACT_MAX_UPDATE_DEPTH_PATTERN.test(error.message)) {
      return true;
    }
    if (error.stack !== undefined && REACT_MAX_UPDATE_DEPTH_PATTERN.test(error.stack)) {
      return true;
    }
    return false;
  }
  return REACT_MAX_UPDATE_DEPTH_PATTERN.test(String(error));
}

export function armRenderStormLatch(
  fingerprint: string,
  latchMs: number = RENDER_STORM_LATCH_DEFAULT_MS,
): void {
  const until = Date.now() + latchMs;
  if (!state.latched || until > state.expiresAtMs) {
    state = { latched: true, expiresAtMs: until, fingerprint };
    try {
      quarantineCartRegistryOnRenderStorm();
    } catch {
      // Registry heal is best-effort — never block latch arm.
    }
    notifyListeners();
  }
}

/**
 * Record a render-storm signature. Returns true when latch is armed (during or after this call).
 */
export function noteRenderStormSignature(error: unknown, source?: string): boolean {
  if (!isReactMaximumUpdateDepthError(error)) {
    return isRenderStormLatched();
  }
  const err = error instanceof Error ? error : new Error(String(error));
  const fingerprint = fingerprintRenderStormError(err, source);
  const now = Date.now();
  const prior = hitTimestamps.get(fingerprint) ?? [];
  const hits = prior.filter((at) => now - at < RENDER_STORM_WINDOW_MS);
  hits.push(now);
  hitTimestamps.set(fingerprint, hits);
  if (hits.length >= RENDER_STORM_HIT_THRESHOLD) {
    armRenderStormLatch(fingerprint);
    return true;
  }
  return isRenderStormLatched();
}

export function isRenderStormLatched(): boolean {
  expireIfNeeded();
  return state.latched;
}

export function getRenderStormLatchFingerprint(): string | null {
  expireIfNeeded();
  return state.fingerprint;
}

export function getRenderStormLatchRemainingMs(): number {
  expireIfNeeded();
  if (!state.latched) {
    return 0;
  }
  return Math.max(0, state.expiresAtMs - Date.now());
}

export function clearRenderStormLatch(): void {
  state = { latched: false, expiresAtMs: 0, fingerprint: null };
  hitTimestamps.clear();
  notifyListeners();
}

export function subscribeRenderStormLatch(listener: RenderStormListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function clearRenderStormLatchForTests(): void {
  clearRenderStormLatch();
}
